'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import type { CarouselApi } from '@/app/_components/ui/carousel';
import { useLabel } from '@/app/_contexts/labels-context';
import { cn } from '@/app/_lib/utils';
import { useCarouselAutoplay } from './use-carousel-autoplay';

// Sliding window configuration
const WINDOW_SIZE = 7;
const THRESHOLD = 7;
const DESKTOP_BREAKPOINT = 1024;

interface CarouselDotsProps {
  api?: CarouselApi;
}

interface VisibleWindow {
  start: number;
  end: number;
  showLeftEllipsis: boolean;
  showRightEllipsis: boolean;
}

function getVisibleWindow(
  current: number,
  total: number,
  windowSize: number
): VisibleWindow {
  const half = Math.floor(windowSize / 2);

  let start = current - half;
  let end = current + half;

  // Correct at edges
  if (start < 0) {
    start = 0;
    end = Math.min(windowSize - 1, total - 1);
  }
  if (end >= total) {
    end = total - 1;
    start = Math.max(0, total - windowSize);
  }

  return {
    start,
    end,
    showLeftEllipsis: start > 0,
    showRightEllipsis: end < total - 1,
  };
}

interface EllipsisButtonProps {
  onClick: () => void;
  label: string;
}

function EllipsisButton({ onClick, label }: EllipsisButtonProps) {
  return (
    <button
      onClick={onClick}
      className="group flex cursor-pointer items-center gap-0.5 p-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      aria-label={label}
    >
      <span className="block size-1 rounded-full bg-muted transition-colors group-hover:bg-muted-foreground/30" />
      <span className="block size-1 rounded-full bg-muted transition-colors group-hover:bg-muted-foreground/30" />
    </button>
  );
}

export function CarouselDots({ api }: CarouselDotsProps) {
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null);
  const buttonsRef = useRef<Map<number, HTMLButtonElement>>(new Map());

  const carouselPaginationLabel = useLabel(
    'carouselPagination',
    'Carousel pagination'
  );
  const goToSlideLabel = useLabel('goToSlide', 'Go to slide');
  const moreSlidesBefore = useLabel('carouselMoreBefore', 'More slides before');
  const moreSlidesAfter = useLabel('carouselMoreAfter', 'More slides after');

  const { resetAutoplay, autoplayContainerHandlers } = useCarouselAutoplay(api);

  const handleDotClick = useCallback(
    (index: number) => {
      resetAutoplay();
      api?.scrollTo(index);
    },
    [api, resetAutoplay]
  );

  // Desktop detection
  useEffect(() => {
    const checkDesktop = () =>
      { setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT); };
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => { window.removeEventListener('resize', checkDesktop); };
  }, []);

  // Read initial state from the Embla API once it's available after mount
  // (api is null on first render). Legitimate external-store init; later
  // updates come through the select/reInit event handlers below.
  useEffect(() => {
    if (!api) return;

    /* eslint-disable react-hooks/set-state-in-effect */
    setScrollSnaps(api.scrollSnapList());
    setCurrentIndex(api.selectedScrollSnap());
    /* eslint-enable react-hooks/set-state-in-effect */

    const onSelect = () => {
      setCurrentIndex(api.selectedScrollSnap());
    };

    const onReInit = () => {
      setScrollSnaps(api.scrollSnapList());
      setCurrentIndex(api.selectedScrollSnap());
    };

    api.on('select', onSelect);
    api.on('reInit', onReInit);

    return () => {
      api.off('select', onSelect);
      api.off('reInit', onReInit);
    };
  }, [api]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      const lastIndex = scrollSnaps.length - 1;
      let targetIndex: number | null = null;

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          targetIndex = index > 0 ? index - 1 : lastIndex;
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          targetIndex = index < lastIndex ? index + 1 : 0;
          break;
        case 'Home':
          event.preventDefault();
          targetIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          targetIndex = lastIndex;
          break;
      }

      if (targetIndex !== null) {
        resetAutoplay();
        api?.scrollTo(targetIndex);
        // Wait for DOM update before focusing (dot may not exist yet in window mode)
        requestAnimationFrame(() => {
          buttonsRef.current.get(targetIndex)?.focus();
        });
      }
    },
    [api, scrollSnaps.length, resetAutoplay]
  );

  if (scrollSnaps.length <= 1) {
    return null;
  }

  // Determine if we should use window mode (show all dots during SSR to avoid layout shift)
  const useWindowMode =
    isDesktop === false && scrollSnaps.length > THRESHOLD;
  const visibleWindow = useWindowMode
    ? getVisibleWindow(currentIndex, scrollSnaps.length, WINDOW_SIZE)
    : null;

  return (
    <div
      // md:ml-auto keeps the dots pinned to the right on desktop even when no
      // navigation arrows are present (the controls bar uses justify-between,
      // which would otherwise left-align a lone dots group). Mobile stays centered.
      className="flex items-center gap-0 md:ml-auto"
      role="group"
      aria-label={carouselPaginationLabel}
      {...autoplayContainerHandlers}
    >
      {visibleWindow?.showLeftEllipsis && (
        <EllipsisButton
          onClick={() => { handleDotClick(visibleWindow.start - 1); }}
          label={moreSlidesBefore}
        />
      )}

      {scrollSnaps.map((_, index) => {
        // Skip dots outside the visible window
        if (
          visibleWindow &&
          (index < visibleWindow.start || index > visibleWindow.end)
        ) {
          return null;
        }

        return (
          <button
            key={index}
            ref={(el) => {
              if (el) {
                buttonsRef.current.set(index, el);
              } else {
                buttonsRef.current.delete(index);
              }
            }}
            onClick={() => { handleDotClick(index); }}
            onKeyDown={(e) => { handleKeyDown(e, index); }}
            className="group cursor-pointer p-2 transition-opacity duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            aria-label={`${goToSlideLabel} ${index + 1} / ${scrollSnaps.length}`}
            aria-current={index === currentIndex ? 'true' : undefined}
          >
            <span
              className={cn(
                'block size-2 rounded-full transition-colors',
                index === currentIndex
                  ? 'bg-primary'
                  : 'bg-muted group-hover:bg-muted-foreground/30'
              )}
            />
          </button>
        );
      })}

      {visibleWindow?.showRightEllipsis && (
        <EllipsisButton
          onClick={() => { handleDotClick(visibleWindow.end + 1); }}
          label={moreSlidesAfter}
        />
      )}
    </div>
  );
}
